/**
 * Team Birthdays Hook
 *
 * Loads the manager's team members whose birthday falls in the current calendar
 * month, for the "Team member birthdays this month" metric + drill-down modal
 * on the Manager Dashboard.
 *
 * Source: `/data/EmployeesV2` — we fetch each managed employee's record (the
 * same per-member fetch `useTeamMembers` uses) and read `BirthDate`. Because a
 * birthday is a recurring month/day regardless of year, the "this month" filter
 * is applied client-side on the month component of BirthDate rather than via an
 * OData date-range filter (which would only match a specific year).
 *
 * The managed personnel numbers come from `useManagerStatus`, so the card only
 * reflects the current manager's team — consistent with the other dashboard
 * metrics.
 *
 * Members are mapped into the same card shape the rest of the Manager Portal
 * uses — { id, personnelNumber, name, role, status, birthDate, birthDay } — so
 * selecting one can open the shared TeamMemberDetailModal.
 *
 * Returns:
 *   - birthdays: Array<{ id, personnelNumber, name, role, status, birthDate, birthDay }>
 *   - count: number
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { employeeService } from "../../api/services";
import useManagerStatus from "./useManagerStatus";

/**
 * D365 uses placeholder dates for "unset" values; treat those as no birthday.
 */
const isPlaceholderDate = (dateStr) =>
  !dateStr ||
  dateStr.startsWith("1900-01-01") ||
  new Date(dateStr).getUTCFullYear() >= 2100;

/**
 * A birth date is a calendar date, not a point in time — D365 returns it as a
 * UTC timestamp (seen as both T00:00:00Z and T12:00:00Z depending on the
 * field/record), so reading the month/day with local getters would roll the
 * date backward a day (or across a month boundary) for anyone browsing from a
 * timezone behind UTC. Always read month/day in UTC instead.
 */
const getUtcMonth = (dateStr) => new Date(dateStr).getUTCMonth();
const getUtcDay = (dateStr) => new Date(dateStr).getUTCDate();

/**
 * Map a raw EmployeesV2 record into the card shape used across the portal.
 * `referenceMonth` (0-indexed) is injectable for testing; defaults to now.
 */
const mapBirthday = (employee, personnelNumber) => {
  const name =
    employee?.Name ||
    employee?.displayName ||
    employee?.fullName ||
    employee?.DisplayName ||
    personnelNumber;

  const isInactive =
    employee?.IsActive === false ||
    employee?.EmploymentStatus === "Inactive" ||
    employee?.WorkerStatus === "Inactive";

  const birthDate = employee?.BirthDate || null;
  // Day-of-month for sorting/display ("15th"); null when unknown.
  const birthDay = isPlaceholderDate(birthDate) ? null : getUtcDay(birthDate);

  return {
    id: personnelNumber,
    personnelNumber,
    name,
    role: employee?.PositionTitle || employee?.Title || "—",
    status: isInactive ? "On Leave" : "Active",
    birthDate: isPlaceholderDate(birthDate) ? null : birthDate,
    birthDay,
  };
};

export default function useTeamBirthdays(referenceMonth = new Date().getMonth()) {
  const {
    managedPersonnelNumbers,
    isLoading: managerStatusLoading,
    error: managerStatusError,
  } = useManagerStatus();

  const {
    data: birthdays,
    isLoading: birthdaysLoading,
    error: birthdaysError,
  } = useQuery(
    ["teamBirthdays", referenceMonth, managedPersonnelNumbers],
    async () => {
      console.log(
        `🎂 Fetching birthdays for ${managedPersonnelNumbers.length} managed person(s), month ${referenceMonth + 1}`
      );

      // Fetch each managed employee in parallel; skip any that fail so one bad
      // record doesn't blank the whole list (mirrors useTeamMembers).
      const results = await Promise.all(
        managedPersonnelNumbers.map(async (personnelNumber) => {
          try {
            const res = await employeeService.getEmployeeByNumber(
              personnelNumber
            );
            const employee = res.data?.value?.[0] || null;
            return mapBirthday(employee, personnelNumber);
          } catch (err) {
            console.error(
              `❌ Failed to fetch birthday for ${personnelNumber}:`,
              err
            );
            return null;
          }
        })
      );

      // Keep only members with a known birthday in the reference month, then
      // sort by day-of-month so the list reads chronologically.
      return results
        .filter(Boolean)
        .filter((m) => m.birthDate && getUtcMonth(m.birthDate) === referenceMonth)
        .sort((a, b) => (a.birthDay ?? 99) - (b.birthDay ?? 99));
    },
    {
      enabled: managedPersonnelNumbers.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    birthdays: birthdays ?? [],
    count: birthdays?.length ?? 0,
    isLoading:
      managerStatusLoading ||
      (managedPersonnelNumbers.length > 0 && birthdaysLoading),
    error: managerStatusError || birthdaysError || null,
  };
}
